import React, { useEffect, useState } from "react";
import FullPageLoader from "../../../common/FullPageLoader";
import { paymentGenerateData,generatePaymentLinkUrl } from '../../../store/slices/insurance';
import * as HELPER from "../../../config/helper";
import { PAYMENT_GENERATE_WEB_SOURCE } from '../../../config/constant';
import ConfirmPopUp from '../../../common/ConfirmPopUp';
import AdditionalDiscountPopup, { AdditionalDiscountData } from '../../../common/AdditionalDiscountPopup';
import PaymentUrlPopup from "./PaymentUrlPopup";
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from "react-redux";
import { useLanguage } from "@src/context/LanguageContext";
import { saveAdditionalDiscount } from '../../../store/slices/insurance';

interface MultiLogo { logo: string }
interface PaymentTenure { name: string; slug: string; is_disable: boolean; amount: number; month: number; application_fee: number; payment_type_tenure_id: number }
interface PaymentMethod { name: string; slug: string; logo: string; is_disable: boolean; multi_logo?: MultiLogo[]; items?: PaymentTenure[],application_fee?:number,payment_type_tenure_id?:number }
interface Category { name: string; slug: string; logo: string; description?: string; items: PaymentMethod[],is_disable?:boolean }

// Static card-network logos (Visa / Mastercard / JCB) shown for every method
// under the Credit Card category (cat.slug === "credit-card"), always used
// for that case regardless of what multi_logo the API sends.
const CREDIT_CARD_LOGOS = [
  "https://imgcdnused.carbay.com/car_image/02026/1768389723383.png",
  "https://imgcdnused.carbay.com/car_image/02026/1768389769557.png",
  "https://imgcdnused.carbay.com/car_image/02026/1768389805775.png",
];

const GeneratePaymentLinkApp = () => {
    const queryParams = useParams();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const type = params.get('type');
    const isRegenerate = type === "regenerate";
    const lead_id = queryParams.id
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
    const [selectedTenure, setSelectedTenure] = useState<PaymentTenure | null>(null);
    const [openAccordion, setOpenAccordion] = useState<string | null>("credit-card");
    const [premiumAmount,setPremiumAmount] = useState<number>(0);
    const [discount,setDiscount] = useState<number>(0);
    const [offsetPointsAmount,setOffsetPointsAmount] = useState<number>(0);
    const [discountPercent,setDiscountPercent] = useState<number>(0);
    const [transactionFee,setTransactionFee] = useState<number>(0)
    const [adminFee,setAdminFee] = useState<number>(0)
    const [totalPaymentAmount,setTotalPaymentAmount] = useState<number>(0)
    const [paymentTypeTenureId,setPaymentTypeTenureId] = useState<number>(0)
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [confirmationMessage, setConfirmationMessage] = useState('Once the link is generated, you cannot create another link using the same payment mode. Are you sure you want to generate payment URL?');
    const [showAdditionalDiscount, setShowAdditionalDiscount] = useState(false);
    const [openAdditionalDiscountOnLoad, setOpenAdditionalDiscountOnLoad] = useState(
      isRegenerate || Boolean(location.state?.showAdditionalDiscount)
    );
    const [additionalDiscountData, setAdditionalDiscountData] = useState<AdditionalDiscountData | undefined>();
    const [showPaymentUrl, setShowPaymentUrl] = useState(false);
    const [paymentUrl,setPaymentUrl] = useState<string>("");
    const [paymentUrlExpiry,setPaymentUrlExpiry] = useState<string>("");
    const insuranceIdState = useSelector((state: any) => state.insuranceId);
    const transactionDetail = insuranceIdState?.insuranceDetail?.transaction_detail
    const navigate = useNavigate();
    const {translations} = useLanguage();
   
  const getPaymentGenerateScreenData = async () => {
    try {
      setLoading(true);
      const result = await paymentGenerateData({ lead_id: lead_id && +lead_id });
      if (result.status === 200) {
        const items = result.data.items;
        setCategories(result.data.items);
        setPremiumAmount(result.data.total_premium_amount)
        setDiscount(result.data.total_discount_amount)
        setOffsetPointsAmount(result.data.offset_points_amount)
        setDiscountPercent(result.data.customer_discount_per || (result.data.total_premium_amount ? (result.data.customer_discount / result.data.total_premium_amount) * 100 : 0))
        setAdminFee(result.data.admin_fee)
        if (openAdditionalDiscountOnLoad) {
          setShowAdditionalDiscount(true);
          setOpenAdditionalDiscountOnLoad(false);
        }
        // ---- AUTO SELECTION LOGIC ----
        if (items.length) {
            const firstCategory = items[0];
            setSelectedCategory(firstCategory);
            setOpenAccordion(firstCategory.slug === "credit-card" ? "credit-card" : null);

            const firstMethod = firstCategory.items?.find((m:Category) => !m.is_disable);
            if (firstMethod) {
                setSelectedMethod(firstMethod);
                setTransactionFee(firstMethod.application_fee || 0);
                setPaymentTypeTenureId(firstMethod.payment_type_tenure_id || 0);
                // If credit card, also auto select first tenure
                if (firstMethod.slug === "visa-mastercard-jcb" && firstMethod.items?.length) {
                    const firstTenure = firstMethod.items.find((t:PaymentTenure) => !t.is_disable);
                    if (firstTenure) {
                        setSelectedTenure(firstTenure);
                        setTransactionFee(firstTenure.application_fee || 0);
                        setPaymentTypeTenureId(firstTenure.payment_type_tenure_id || 0);
                    }
                }
            }
        }
      }
    } catch (error) {
      console.log("Error fetching:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { getPaymentGenerateScreenData(); }, []);

  const handleCategoryClick = (cat: Category) => {
  setOpenAccordion(openAccordion === cat.slug ? null : cat.slug);
  setSelectedCategory(cat);
  setSelectedMethod(null);
  setSelectedTenure(null);
  setTransactionFee(0);
  setPaymentTypeTenureId(0);
};

  const handleMethodSelect = (method: PaymentMethod,cat:Category) => {
    setSelectedMethod(method);
    setSelectedCategory(cat);
    if(method.application_fee){
        setTransactionFee(method.application_fee)
    }
    if(method.payment_type_tenure_id){
        setPaymentTypeTenureId(method.payment_type_tenure_id);
    }
    setSelectedTenure(null);
  };

  const handleTenureSelect = (tenure: PaymentTenure) => {
    setSelectedTenure(tenure);
    if(tenure.application_fee){
        setTransactionFee(tenure.application_fee)
    }
    if(tenure.payment_type_tenure_id){
        setPaymentTypeTenureId(tenure.payment_type_tenure_id);
    }
  }

    useEffect(() => {
        const total =
        premiumAmount - discount - offsetPointsAmount + transactionFee + adminFee;
        setTotalPaymentAmount(total);
    }, [premiumAmount, discount, offsetPointsAmount, transactionFee, adminFee]);

    const generatePaymentLink = async (additionalDiscountData?: AdditionalDiscountData) => {
        try{
            setLoading(true);
            const payload = {
              "lead_id":lead_id,
              "payment_type_tenure_id":paymentTypeTenureId,
              "regenerate":isRegenerate?1:0,
              "source":PAYMENT_GENERATE_WEB_SOURCE,
              ...(additionalDiscountData ? {
                "customer_discount_per": additionalDiscountData.customerDiscountPercent,
                "customer_discount_amount": additionalDiscountData.customerDiscountAmount,
                "offset_points_per": additionalDiscountData.offsetPointsPercent,
                "offset_points_tax_per": additionalDiscountData.offsetPointsTaxPercent,
                "net_offset_points_per": additionalDiscountData.netOffsetPointsPercent,
                "agent_commission_per": additionalDiscountData.agentCommissionPercent,
                "offset_points_amount": additionalDiscountData.offsetPointsAmount,
                "premium_amount_payable": additionalDiscountData.premiumAmountPayable,
                "net_commission_earned": additionalDiscountData.netCommissionEarned,
              } : {})
            }
            const result = await generatePaymentLinkUrl(payload);
            if (result.status === 200) {
                setPaymentUrl(result.data.generated_url);
                setPaymentUrlExpiry(result.data.wpc_expiry_date);
                getPaymentGenerateScreenData();
                setShowConfirmation(false);
                setShowPaymentUrl(true);
            }
        }catch(error){
            console.log("Erron in generating Link:",error)
        }finally{
            setLoading(false);
        }
    }

    const confirmNo = () => {
        setShowConfirmation(false);
    }
    const confirmYes = () => {
      setShowConfirmation(false);
      if (additionalDiscountData) {
        generatePaymentLink(additionalDiscountData);
        return;
      }
      generatePaymentLink();
    }
    const handleAdditionalDiscountClose = () => {
      setShowAdditionalDiscount(false);
    }
    const handleChoosePaymentMethod = (discountData: AdditionalDiscountData) => {
      setAdditionalDiscountData(discountData);
      setShowAdditionalDiscount(false);
    }
    const handleClose = () => {
      setShowPaymentUrl(false);
    }

    const handleSaveAdditionalDiscount = async (discountData: AdditionalDiscountData) => {
      try {
        setLoading(true);
        const result = await saveAdditionalDiscount({
          lead_id: lead_id && +lead_id,
          offset_points: discountData.offsetPointsPercent,
          customer_discount_type: discountData.customerDiscountType,
          customer_discount_per: discountData.customerDiscountType === 1 ? discountData.customerDiscountPercent : 0,
          customer_discount: discountData.customerDiscountType === 2 ? discountData.customerDiscountAmount : 0,
          offset_points_amount: discountData.offsetPointsAmount,
          offset_points_type: discountData.offsetPointsType,
        });
        if (result?.status === 200) {
          handleChoosePaymentMethod(discountData);
        }
      } catch (error) {
        console.error("Error saving additional discount:", error);
      } finally {
        setLoading(false);
      }
    }

  return (
    <>
        <FullPageLoader show={loading} />
        {showConfirmation === false ? ("") : (
            <ConfirmPopUp confirmNo={confirmNo} confirmYes={confirmYes} message={confirmationMessage} />
        )}
        <AdditionalDiscountPopup
          show={showAdditionalDiscount}
          leadId={lead_id || 0}
          existingDiscountPercent={discountPercent}
          premiumAmount={premiumAmount}
          transactionFee={transactionFee}
          adminFee={adminFee}
          agentCommissionPercent={Number(insuranceIdState?.insuranceDetail?.agent_commision || 0)}
          handleClose={handleAdditionalDiscountClose}
          handleChoosePaymentMethod={handleSaveAdditionalDiscount}
        />
        {
            showPaymentUrl && <PaymentUrlPopup show = {showPaymentUrl} handleClose={() => { handleClose()}} 
            onRefresh={() => setShowPaymentUrl(false)} paymentUrl={paymentUrl} expiryDate={paymentUrlExpiry}
            />
        }
      <div className="container-fluid">
        <div className="row">
        <div className="generate-payment-link-outer">
          <div className="col-md-12">
            <div className="payment-link-heading">
              <i onClick={() => { navigate('/insurance-list?datetype=mtd&status_type=policy_issued') }} className="ic-arrow_back"></i>
              <h2>{translations?.GENERATE_PAYMENT?.GENERATE}</h2>
            </div>
          </div>
          {/* <div className="row"> */}
            <div className="">
              <div className="payment-detail-outer">
                <div className="payment-detail-bg">
                  <ul>
                    <li><label>{translations?.GENERATE_PAYMENT?.PREMIUM_PAYMENT}</label><span>+ {HELPER.addCurrencyFormatting(premiumAmount)}</span></li>
                    <li><label>{translations?.GENERATE_PAYMENT?.DISCOUNT}</label><span>- {HELPER.addCurrencyFormatting(discount)}</span></li>
                    <li><label>{translations?.GENERATE_PAYMENT?.OFFSET_POINTS}</label><span>- {HELPER.addCurrencyFormatting(offsetPointsAmount)}</span></li>
                    <li><label>{translations?.GENERATE_PAYMENT?.TRANSACTION_FEES}</label><span>+ {HELPER.addCurrencyFormatting(transactionFee)}</span></li>
                    <li><label>{translations?.GENERATE_PAYMENT?.ADMIN_FEES}</label><span>+ {HELPER.addCurrencyFormatting(adminFee)}</span></li>
                    <li><label>{translations?.GENERATE_PAYMENT?.TOTAL_AMOUNT}</label><span>{HELPER.addCurrencyFormatting(totalPaymentAmount)}</span></li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="">
              <div className="payment-category-method-outer">
                <div className="select-payment-category">
                  <h4>{translations?.GENERATE_PAYMENT?.SELECT_CATEGORY}</h4>
                  <div className="bg-white">
                  <ul>
                    {categories.map(cat => {
                      const isOpen = openAccordion === cat.slug;
                      const isActive = selectedCategory?.slug === cat.slug;

                      return (
                        <React.Fragment key={cat.slug}>
                          {/* CATEGORY ROW */}
                          <li className={isActive ? "selected" : ""} onClick={() => handleCategoryClick(cat)}>
                            <img src={cat.logo} />
                            <span>{cat.name}</span>
                            <i className={`ic-keyboard_arrow_right ${isOpen ? "rotate" : ""}`}></i>
                            {cat.description && <label className="sub-heading">{cat.description}</label>}
                          </li>

                          {/* METHODS */}
                          {isOpen && cat.items?.map(method => (
                            <React.Fragment key={method.slug}>
                              <li className={`${selectedMethod?.slug === method.slug ? "selected selected-sub-tab" : ""} ${method.is_disable ? "disabled" : ""}`}>
                                <div className="radio-btn payment-radio-btn">
                                  <div className="custom-control custom-checkbox">
                                    <input
                                      type="radio"
                                      className="custom-control-input"
                                      name={`method-${cat.slug}`}
                                      checked={selectedMethod?.slug === method.slug}
                                      disabled={method.is_disable}
                                      onChange={() => handleMethodSelect(method, cat)}
                                    />
                                    <label className="custom-control-label">{method.name}</label>
                                  </div>

                                  {cat.slug === "credit-card"
                                    ? <div className="multi-card-logos">
                                      {CREDIT_CARD_LOGOS.map((src, i) =>
                                        <img key={i} src={src} style={{ height: 17 }} />
                                      )}
                                    </div>
                                    : method.multi_logo?.length
                                    ? <div className="multi-card-logos">
                                      {method.multi_logo.map((img, i) =>
                                        <img key={i} src={img.logo} style={{ height: 17 }} />
                                      )}
                                    </div>
                                    : <img src={method.logo} style={{ height: 25 }}/>}
                                </div>

                              {/* TENURE under selected card */}
                              <ul>
                              {selectedMethod?.slug === method.slug &&
                                method.items?.map(tenure => (
                                  <li key={tenure.slug}
                                    className={`child-tenure ${selectedTenure?.slug === tenure.slug ? "selected" : ""} ${tenure.is_disable ? "disabled" : ""}`}>
                                    <div className="radio-btn payment-radio-btn">
                                      <div className="custom-control custom-checkbox">
                                        <input
                                          type="radio"
                                          className="custom-control-input"
                                          name={`tenure-${method.slug}`}
                                          checked={selectedTenure?.slug === tenure.slug}
                                          disabled={tenure.is_disable}
                                          onChange={() => handleTenureSelect(tenure)}
                                        />
                                        <label className="custom-control-label">{tenure.name}</label>
                                      </div>
                                      <span>{tenure.amount} {tenure.month ? `X ${tenure.month} mo` : ""}</span>
                                    </div>
                                  </li>
                                ))
                              }
                              </ul>
                              </li>

                            </React.Fragment>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </ul>
                  </div>
                </div>
              </div>
            </div>

          
          {/* </div> */}
        </div>
            <div className="generate-btn-bottom btn-bottom-fixed">
                      <div className="warning-txt">
                        <i className="ic-warning"></i>
                        <span>{translations?.GENERATE_PAYMENT?.PARAGRAPH}</span>
                      </div>
                      <button
                        className="btn-primary"
                        disabled={ 
                          transactionDetail?.payment_status_slug==='paid' 
                          || 
                          (!selectedMethod 
                            || 
                            (selectedMethod.slug === "visa-mastercard-jcb" && !selectedTenure)
                          )}
                        onClick={() => setShowConfirmation(true)}>
                        {translations?.GENERATE_PAYMENT?.GENERATE}
                      </button>
                    </div>
        </div>
      </div>
    </>
  );
};

export default GeneratePaymentLinkApp;
